import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ExternalLink, ArrowLeft, Share2, Sparkles } from 'lucide-react';
import { API } from '@/App';

export default function SharedChecklistPage() {
  const { checklistId } = useParams();
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchChecklist();
  }, [checklistId]);

  // Set OG meta tags dynamically
  useEffect(() => {
    if (checklist) {
      document.title = `${checklist.name} — Gloverse Checklist`;
      const setMeta = (property, content) => {
        let el = document.querySelector(`meta[property="${property}"]`);
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute('property', property);
          document.head.appendChild(el);
        }
        el.setAttribute('content', content);
      };
      const desc = `${checklist.product_details?.length || 0} products curated on Gloverse`;
      setMeta('og:title', `${checklist.name} — Gloverse`);
      setMeta('og:description', desc);
      setMeta('og:url', window.location.href);
      setMeta('og:type', 'website');
    }
    return () => { document.title = 'Gloverse'; };
  }, [checklist]);

  const fetchChecklist = async () => {
    try {
      const response = await fetch(`${API}/checklists/${checklistId}`);
      if (response.ok) {
        setChecklist(await response.json());
      } else {
        setError('Checklist not found');
      }
    } catch (e) {
      setError('Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  const defaultImg = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 48 48'%3E%3Crect width='48' height='48' rx='8' fill='%23f3e8ff'/%3E%3Ctext x='50%25' y='55%25' font-size='20' text-anchor='middle' dominant-baseline='middle' fill='%23a855f7'%3EG%3C/text%3E%3C/svg%3E";

  const trackClick = async (product) => {
    try {
      await fetch(`${API}/analytics/track-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: product.product_id,
          product_name: product.name,
          brand: product.brand,
          purchase_url: product.purchase_url,
          source: 'shared_checklist',
          checklist_id: checklistId
        })
      });
    } catch { /* silent */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !checklist) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 text-center">
        <Sparkles className="w-12 h-12 text-muted-foreground/40 mb-4" />
        <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{error || 'Not found'}</h2>
        <p className="text-muted-foreground mb-6">This checklist may have been removed or is no longer available.</p>
        <button onClick={() => navigate('/')} className="px-6 py-2.5 bg-primary text-white rounded-full text-sm font-medium">
          Go to Gloverse
        </button>
      </div>
    );
  }

  const products = checklist.product_details || [];

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold truncate mx-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {checklist.name}
          </h1>
          <div className="w-9" />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Checklist info */}
        <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-5 mb-6">
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'hsl(330, 85%, 50%)' }}>
            {checklist.name}
          </h2>
          <div className="flex flex-wrap gap-2 mb-3">
            {checklist.filters?.skin_type && (
              <span className="px-3 py-1 bg-white/70 rounded-full text-xs font-medium text-muted-foreground">
                {checklist.filters.skin_type}
              </span>
            )}
            {checklist.filters?.category && (
              <span className="px-3 py-1 bg-white/70 rounded-full text-xs font-medium text-muted-foreground">
                {checklist.filters.category}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{products.length} products curated on Gloverse</p>
        </div>

        {/* Products */}
        <div className="space-y-3">
          {products.map((product, idx) => (
            <div key={product.product_id} className="bg-white rounded-xl border border-border overflow-hidden">
              <div className="flex items-center gap-3 p-3">
                <span className="text-lg font-bold text-primary/40 w-7 text-center shrink-0">{idx + 1}</span>
                {product.image ? (
                  <img src={product.image} alt={product.name} className="w-12 h-12 rounded-lg object-cover shrink-0" onError={(e) => { e.target.src = defaultImg; }} />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-200 to-pink-100 flex items-center justify-center text-purple-500 font-bold text-lg shrink-0" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    G
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{product.name}</p>
                  <p className="text-xs text-muted-foreground">{product.brand} {product.product_type ? `\u00B7 ${product.product_type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}` : ''}</p>
                  {product.price && <p className="text-sm font-bold text-primary mt-0.5">{product.price}</p>}
                </div>
              </div>
              {product.purchase_url && (
                <a
                  href={product.purchase_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackClick(product)}
                  data-testid={`shared-buy-${product.product_id}`}
                  className="flex items-center gap-2 px-4 py-2.5 bg-primary/5 text-primary text-sm font-medium hover:bg-primary/10 transition-colors border-t border-border"
                >
                  <ExternalLink className="w-4 h-4" />
                  Shop Now
                </a>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground mb-3">Create your own beauty checklists</p>
          <button
            onClick={() => navigate('/')}
            data-testid="join-gloverse-btn"
            className="px-8 py-3 bg-primary text-white rounded-full text-sm font-semibold shadow-md hover:shadow-lg transition-all"
          >
            Join Gloverse
          </button>
        </div>
      </div>
    </div>
  );
}
